import { useState, useEffect, useMemo } from 'react';
import MiniSearch from 'minisearch';
import type { SearchIndexEntry, InsightCategory } from '@claude-code-insights/shared';
import { INSIGHT_CATEGORIES } from '@claude-code-insights/shared';
import { fetchSearchIndex, semanticSearch, type SemanticSearchResult } from '../lib/api.js';
import { InsightCard } from '../components/InsightCard.js';
import { Link } from 'react-router-dom';

export function SearchView() {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState<SearchIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<InsightCategory | ''>('');
  const [searchMode, setSearchMode] = useState<'keyword' | 'semantic'>('keyword');
  const [semanticResults, setSemanticResults] = useState<SemanticSearchResult[]>([]);
  const [semanticLoading, setSemanticLoading] = useState(false);

  useEffect(() => {
    fetchSearchIndex()
      .then(setIndex)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (searchMode !== 'semantic' || !query.trim()) {
      setSemanticResults([]);
      return;
    }
    setSemanticLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await semanticSearch(query.trim());
        setSemanticResults(results);
      } catch {
        setSemanticResults([]);
      } finally {
        setSemanticLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query, searchMode]);

  const miniSearch = useMemo(() => {
    const ms = new MiniSearch<SearchIndexEntry>({
      fields: ['title', 'summary', 'tags'],
      storeFields: ['id', 'title', 'summary', 'category', 'tags', 'relevanceScore', 'sourceType', 'createdAt'],
      searchOptions: {
        boost: { title: 2 },
        fuzzy: 0.2,
        prefix: true,
      },
    });
    ms.addAll(index);
    return ms;
  }, [index]);

  const results = useMemo(() => {
    if (!query.trim()) {
      const sorted = [...index].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      if (categoryFilter) {
        return sorted.filter((item) => item.category === categoryFilter);
      }
      return sorted.slice(0, 50);
    }

    let searchResults = miniSearch.search(query) as unknown as SearchIndexEntry[];
    if (categoryFilter) {
      searchResults = searchResults.filter(
        (item) => item.category === categoryFilter,
      );
    }
    return searchResults;
  }, [query, index, miniSearch, categoryFilter]);

  return (
    <div className="p-3 md:p-6 max-w-4xl mx-auto">
      <h2 className="text-lg font-semibold mb-4">Search Insights</h2>

      <div className="flex flex-col md:flex-row gap-3 mb-4 md:mb-6">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title, summary, or tags..."
          className="input flex-1"
          autoFocus
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as InsightCategory | '')}
          className="input"
        >
          <option value="">All categories</option>
          {INSIGHT_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setSearchMode('keyword')}
          className={searchMode === 'keyword' ? 'btn-primary text-xs px-3 py-1' : 'btn-ghost text-xs px-3 py-1'}
        >
          🔤 Keyword
        </button>
        <button
          onClick={() => setSearchMode('semantic')}
          className={searchMode === 'semantic' ? 'btn-primary text-xs px-3 py-1' : 'btn-ghost text-xs px-3 py-1'}
        >
          🧠 Semantic
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-obsidian-text-muted">
          Loading search index...
        </div>
      ) : searchMode === 'semantic' ? (
        <div>
          {semanticLoading && <p className="text-sm text-gray-500 py-4">Searching...</p>}
          {!semanticLoading && semanticResults.length === 0 && query.trim() && (
            <p className="text-sm text-gray-500 py-4">No results found</p>
          )}
          {semanticResults.map(({ insight, score }) => (
            <div key={insight.insightId} className="relative">
              <span
                className="absolute top-3 right-3 text-xs px-2 py-0.5 rounded z-10"
                style={{
                  background: '#1a1a2e',
                  color: score > 0.85 ? '#7f6df2' : score > 0.7 ? '#9d8fe0' : '#6a6aaa',
                }}
              >
                {Math.round(score * 100)}% match
              </span>
              <InsightCard insight={insight} />
            </div>
          ))}
        </div>
      ) : (
        <>
          <p className="text-sm text-obsidian-text-faint mb-4">
            {results.length} result{results.length !== 1 ? 's' : ''}
            {query && ` for "${query}"`}
          </p>

          <div className="space-y-2">
            {results.map((item) => (
              <Link
                key={item.id}
                to={`/insight/${item.id}`}
                className="card block"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                      item.relevanceScore >= 8
                        ? 'bg-obsidian-success/20 text-obsidian-success'
                        : item.relevanceScore >= 5
                          ? 'bg-obsidian-accent/20 text-obsidian-accent'
                          : 'bg-obsidian-warning/20 text-obsidian-warning'
                    }`}
                  >
                    {item.relevanceScore}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium truncate">{item.title}</h3>
                    <p className="text-xs text-obsidian-text-muted truncate mt-0.5">
                      {item.summary}
                    </p>
                  </div>
                  <span className="badge bg-obsidian-bg-tertiary text-obsidian-text-faint text-xs">
                    {item.category}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {results.length === 0 && query && (
            <div className="py-8 text-center text-obsidian-text-muted text-sm">
              No results for "{query}"
            </div>
          )}
        </>
      )}
    </div>
  );
}
